import type { OutcomeSummary } from '@/lib/api';

function getOriginalOrder(outcomes: OutcomeSummary[]): Map<string, number> {
  return new Map(outcomes.map((outcome, index) => [outcome.id, index]));
}

export function getBlockedDependencies(
  outcome: OutcomeSummary,
  outcomes: OutcomeSummary[]
): string[] {
  const outcomesById = new Map(outcomes.map(item => [item.id, item]));
  return (outcome.dependsOn ?? []).filter(depId => outcomesById.get(depId)?.status !== 'complete');
}

export function computeDependencyLayers(outcomes: OutcomeSummary[]): OutcomeSummary[][] {
  if (outcomes.length === 0) return [];

  const originalOrder = getOriginalOrder(outcomes);
  const outcomesById = new Map(outcomes.map(outcome => [outcome.id, outcome]));
  const inDegree = new Map<string, number>();
  const outgoing = new Map<string, string[]>();
  const missingDependencyOutcomes = new Set<string>();

  for (const outcome of outcomes) {
    inDegree.set(outcome.id, 0);
    outgoing.set(outcome.id, []);
  }

  for (const outcome of outcomes) {
    for (const depId of outcome.dependsOn ?? []) {
      if (!outcomesById.has(depId)) {
        missingDependencyOutcomes.add(outcome.id);
        continue;
      }
      inDegree.set(outcome.id, (inDegree.get(outcome.id) ?? 0) + 1);
      outgoing.get(depId)?.push(outcome.id);
    }
  }

  let currentLayer = outcomes
    .filter(
      outcome => !missingDependencyOutcomes.has(outcome.id) && (inDegree.get(outcome.id) ?? 0) === 0
    )
    .sort((a, b) => (originalOrder.get(a.id) ?? 0) - (originalOrder.get(b.id) ?? 0));

  const layers: OutcomeSummary[][] = [];
  const visited = new Set<string>();

  while (currentLayer.length > 0) {
    layers.push(currentLayer);
    const nextLayerIds = new Set<string>();

    for (const outcome of currentLayer) {
      visited.add(outcome.id);
      for (const dependentId of outgoing.get(outcome.id) ?? []) {
        const remaining = (inDegree.get(dependentId) ?? 0) - 1;
        inDegree.set(dependentId, remaining);
        if (remaining === 0) nextLayerIds.add(dependentId);
      }
    }

    currentLayer = [...nextLayerIds]
      .map(id => outcomesById.get(id))
      .filter((outcome): outcome is OutcomeSummary => !!outcome)
      .sort((a, b) => (originalOrder.get(a.id) ?? 0) - (originalOrder.get(b.id) ?? 0));
  }

  const remaining = outcomes.filter(outcome => !visited.has(outcome.id));
  if (remaining.length > 0) {
    layers.push(remaining);
  }

  return layers;
}
